import React from 'react';
import { Link } from 'react-router-dom';

// Mini version timeline — the draft's lineage as a horizontal rail, NEWEST
// FIRST so the freshest drafts are visible without scrolling (the rail still
// scrolls back through history). Nodes are deliberately dense; the tooltip
// carries the full story.
//
// Two modes:
//  - link mode (dashboard): pass `base` — nodes deep-link to `${base}?v=<id>`
//  - picker mode (document view): pass `onSelect` — nodes switch the viewed
//    version in place; `viewingId` / `compareToId` mark where the reader is
//    on the timeline and what the redline is measured against.
//
// Versions NOT on the current draft's ancestry (baseVersionId chain) are
// dead branches — e.g. the loser of a concurrent publish that was later
// reconciled. They stay visible (immutable history) but render dimmed and
// dotted so duplicate numbers stop looking like data corruption.
//
// Every version is stamped date AND time — v1 and v2 usually land the same
// day, so the date alone can't tell them apart.
const stamp = (t) => {
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  return `${day} · ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

export default function VersionRail({ base, doc, versions, partyColors, viewingId, compareToId, onSelect }) {
  const sorted = [...(versions || [])].sort((a, b) => b.number - a.number || b.createdAt - a.createdAt);
  const ingesting = doc.status === 'queued' || doc.status === 'processing';
  if (!sorted.length && !ingesting) return null;

  // Ancestry of the official draft: anything off this chain is a branch.
  const byId = new Map(sorted.map((v) => [v.id, v]));
  const lineage = new Set();
  let node = byId.get(doc.currentVersionId);
  while (node && !lineage.has(node.id)) {
    lineage.add(node.id);
    node = node.baseVersionId ? byId.get(node.baseVersionId) : null;
  }

  return (
    <div className="cs-vrail" role="list" aria-label="Version timeline — newest first">
      {ingesting && (
        <>
          <span className="cs-vrail-node ingesting" role="listitem">
            <strong className="u-tabular">v{(doc.versionCount || 1) + 1}</strong>
            <span className="cs-vrail-label">ingesting…</span>
          </span>
          {sorted.length > 0 && <span className="cs-vrail-link" />}
        </>
      )}
      {sorted.map((v, i) => {
        const isCurrent = v.id === doc.currentVersionId;
        const isViewing = viewingId === v.id;
        const isBase = compareToId === v.id;
        const branch = lineage.size > 0 && !lineage.has(v.id);
        const cls =
          `cs-vrail-node${isCurrent ? ' current' : ''}${isViewing ? ' viewing' : ''}${isBase ? ' base' : ''}${branch ? ' branch' : ''}`;
        const sealed = !!(v.sealed && v.checkpoint);
        const tip = `${v.label || 'untitled'} — ${v.authorName} · ${new Date(v.createdAt).toLocaleString()}`
          + `${sealed ? ` · sealed ⛓ ${v.checkpoint.chainHash.slice(0, 10)}…` : ' · seal pending'}`
          + (branch ? ' · superseded branch (a concurrent edit won the draft; these changes were reconciled into a later version)' : '')
          + ` · ${onSelect ? 'view this draft' : 'open this version'}`;
        const inner = (
          <>
            <span className="u-cluster" style={{ '--gap': '4px', alignItems: 'center' }}>
              <strong className="u-tabular">v{v.number}</strong>
              {sealed && (
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="var(--ok)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-label="Sealed checkpoint" style={{ flexShrink: 0 }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              )}
              {v.partyKey && (
                <span className="cs-sidebar-dot" style={{ background: partyColors[v.partyKey] || 'var(--muted)' }} />
              )}
              {branch && <span className="cs-vrail-flag u-muted">branch</span>}
              {isViewing && <span className="cs-vrail-flag" style={{ color: 'var(--surf)' }}>viewing</span>}
              {isBase && !isViewing && <span className="cs-vrail-flag u-muted">against</span>}
              {isCurrent && !isViewing && !isBase && <span className="cs-vrail-flag" style={{ color: 'var(--surf)' }}>current</span>}
            </span>
            <span className="cs-vrail-label u-muted">{v.label || v.authorName}</span>
            <span className="cs-vrail-stamp u-muted u-tabular">{stamp(v.createdAt)}</span>
          </>
        );
        return (
          <React.Fragment key={v.id}>
            {i > 0 && <span className="cs-vrail-link" />}
            {onSelect ? (
              <button type="button" role="listitem" className={cls} data-tip={tip} onClick={() => onSelect(v)}>
                {inner}
              </button>
            ) : (
              <Link role="listitem" className={cls} to={`${base}?v=${v.id}`} data-tip={tip}>
                {inner}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
